/*
 * Modal de Materialize, con la misma API que exponia ngx-materialize: mz-modal con
 * [fixedFooter] y [fullscreen], los tres bloques de contenido, openModal()/closeModal()
 * y la clase base MzBaseModal con la propiedad modalComponent.
 *
 * Las 220 etiquetas de modal del proyecto se apoyan en esta forma exacta.
 */
import { Component, Directive, Input, OnDestroy, ViewEncapsulation, inject, ElementRef } from '@angular/core';

/** Opciones del modal de Materialize 0.100, tal como las declara el proyecto. */
export interface ModalOptions {
	dismissible?: boolean;
	opacity?: number;
	inDuration?: number;
	outDuration?: number;
	startingTop?: string;
	endingTop?: string;
	ready?: () => void;
	complete?: () => void;
}

declare const $: ((el: unknown) => {
	modal?: (opciones?: ModalOptions | string) => void;
}) | undefined;

@Component({
	selector: 'mz-modal',
	encapsulation: ViewEncapsulation.None,
	template: `
		<div class="modal" [class.modal-fixed-footer]="fixedFooter" [class.bottom-sheet]="bottomSheet"
				[style.width]="fullscreen ? '95%' : null" [style.max-height]="fullscreen ? '95%' : null">
			<ng-content />
		</div>
	`,
})
export class MzModalComponent implements OnDestroy {
	@Input() fixedFooter = false;
	@Input() fullscreen = false;
	@Input() bottomSheet = false;
	@Input() options?: ModalOptions;

	private readonly host = inject(ElementRef<HTMLElement>);

	private get elemento(): HTMLElement | null {
		return (this.host.nativeElement as HTMLElement).querySelector('.modal');
	}

	openModal(): void {
		this.invoca(this.options ?? {});
		this.invoca('open');
	}

	closeModal(): void {
		this.invoca('close');
	}

	ngOnDestroy(): void {
		this.closeModal();
	}

	private invoca(arg: ModalOptions | string): void {
		const el = this.elemento;
		if (!el || typeof $ !== 'function') {
			return;
		}

		/*
		 * Los metodos por nombre ('open', 'close') no comprueban que el modal este
		 * inicializado. materialize.js hace, literalmente:
		 *
		 *     return this.each(function () { this.M_Modal[methodOrOptions](); });
		 *
		 * `M_Modal` solo existe despues de que Modal.init() haya corrido, es decir
		 * despues de un openModal(). Un modal que se pinto y nunca se abrio -que es lo
		 * normal: estan en la plantilla esperando- no lo tiene, y al destruir el
		 * componente este ngOnDestroy llamaba a close() sobre undefined:
		 *
		 *     TypeError: Cannot read properties of undefined (reading 'close')
		 *
		 * Aparecia al salir de `alertas`, pero le pasaba a cualquier pantalla con un
		 * modal sin abrir. Pasar un objeto si es seguro: esa rama inicializa.
		 */
		if (typeof arg === 'string' && !(el as unknown as { M_Modal?: unknown }).M_Modal) {
			return;
		}

		const jq = $(el);
		if (typeof jq.modal === 'function') {
			jq.modal(arg);
		}
	}
}

@Component({
	selector: 'mz-modal-header',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="modal-header"><ng-content /></div>`,
})
export class MzModalHeaderComponent {}

@Component({
	selector: 'mz-modal-content',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="modal-content"><ng-content /></div>`,
})
export class MzModalContentComponent {}

@Component({
	selector: 'mz-modal-footer',
	encapsulation: ViewEncapsulation.None,
	template: `<div class="modal-footer"><ng-content /></div>`,
})
export class MzModalFooterComponent {}

/**
 * Clase base de los componentes que se abren como modal. ngx-materialize la usaba para
 * dar acceso a modalComponent; los 13 componentes modaledit y deletemodal del proyecto
 * la extienden y llaman a this.modalComponent.closeModal().
 */
export abstract class MzBaseModal {
	/** Lo inyecta el servicio que abre el modal. */
	modalComponent!: MzModalComponent;
}

/**
 * [mz-modal-close]: 66 usos en 21 plantillas del proyecto, sobre los botones CANCELAR y
 * ACEPTAR de cada modal.
 *
 * Materialize cierra el modal al hacer clic en cualquier descendiente con la clase
 * .modal-close; ngx-materialize exponia esta directiva solo para ponerla. Falto en los
 * tres primeros spikes y NO lo detecta el compilador: es un atributo plano, sin
 * corchetes, sobre un <button>, y Angular solo rechaza elementos desconocidos y
 * propiedades ligadas desconocidas. El build quedaba verde y los botones no cerraban.
 */
@Directive({
	selector: '[mz-modal-close]',
	host: { class: 'modal-close' },
})
export class MzModalCloseDirective {}

export const MZ_MODAL = [
	MzModalComponent,
	MzModalHeaderComponent,
	MzModalContentComponent,
	MzModalFooterComponent,
	MzModalCloseDirective,
] as const;
